import { useState, useMemo, memo, Fragment } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Button,
  TextField,
  Autocomplete,
  Chip,
  Popover,
  TableSortLabel,
  TableContainer,
  useTheme,
  useMediaQuery
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AddIcon from "@mui/icons-material/Add";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { formatDate } from "./formatDate";
import InfluencerReviewModal from "./InfluencerReviewModal";
import { LANGUAGE_OPTIONS } from "./languageOptions";
import { canRefreshInfluencer, getCooldownInfo } from "./refreshEligibility";
import { formatCompact, formatUsername } from "./influencerFormat";

// Ordered the way influencer-marketing platforms typically scan a creator row:
// audience reach and engagement quality first (the "is this creator good"
// signals), then cost/efficiency (what it'd take to work with them and whether
// that's a good deal), then descriptive/classification metadata, then internal
// ops fields and freeform notes last — supporting detail, not decision data.
// sortable defaults to true — only explicitly excluded for columns with no
// single natural sort value: totalCost renders as N dynamic per-deliverable
// columns (no one "cost" to sort by), language is a multi-select array, and
// reason is freeform notes text not meaningfully orderable.
const ALL_COLUMNS = [
  { key: "followers", label: "Followers" },
  { key: "likesAvg", label: "Likes (Avg)" },
  { key: "viewsAvg", label: "Views (Avg)" },
  // defaultHidden: less-consulted metrics, out of the way until someone
  // opts back in from Columns Visible (see PlanSummary.jsx's initial
  // visibleColumns state) — off by default on every plan, not just this one.
  { key: "commentsAvg", label: "Comments (Avg)", defaultHidden: true },
  { key: "sharesAvg", label: "Shares (Avg)", defaultHidden: true },
  { key: "repostsAvg", label: "Reposts (Avg)", defaultHidden: true },
  { key: "engagementRate", label: "Engagement Rate" },
  { key: "totalCost", label: "Influencer Cost", sortable: false },
  { key: "brandCost", label: "Brand Cost", sortable: false },
  { key: "cpv", label: "CPV" },
  { key: "cpe", label: "CPE" },
  { key: "lastPaidCost", label: "Last Cost" },
  { key: "lastPlanCost", label: "Last Plan Cost" },
  { key: "reviews", label: "Reviews", sortable: false },
  { key: "contactNumber", label: "Contact Number", sortable: false },
  { key: "email", label: "Email", sortable: false },
  { key: "category", label: "Category" },
  { key: "language", label: "Language", sortable: false },
  { key: "location", label: "Location" },
  { key: "poc", label: "POC" },
  { key: "reason", label: "Reason", sortable: false },
  // Last on purpose — a freshly-added custom column should land at the far
  // right of the table (the "new column" spot, same convention Google Sheets
  // uses), not interrupt the existing Cost/CPV/metadata columns.
  { key: "customFields", label: "Custom Columns", sortable: false },
];

// Distinct accent per dynamic cost-column group — deliverable names (e.g. "1
// Collab Reel") can be identical or similar-looking under both Influencer
// Cost and Brand Cost, so the group name/color is repeated on every column in
// the group (not just the first, where the picker icon already lives) to
// stay identifiable while scanning across or scrolling.
const INFLUENCER_COST_ACCENT = "#FF5200";
const BRAND_COST_ACCENT = "#6366F1";
// Same per-group accent mechanism as above, for the freeform Custom Columns
// group (see activeCustomFields/customFields) — a third, visually distinct
// color so it reads as its own group while scanning across the table.
const CUSTOM_FIELD_ACCENT = "#0EA5E9";

// Spreadsheet-style cell grid (per founder request) — one shared line color so
// every header/body cell traces both its row and column, the same way a
// Google Sheet does. Header gets a heavier bottom rule to read as the frozen
// row; everything else is a uniform 1px line.
const GRID_LINE_COLOR = "rgba(0,0,0,0.09)";

// Checkbox column's width, fixed rather than left to the responsive px in
// BODY_CELL_STYLES/HEADER_CELL_STYLES — the frozen Name column's sticky
// `left` offset (below) has to line up with it exactly, which only holds if
// this column's width can't drift across breakpoints.
const STICKY_CHECKBOX_WIDTH = 48;

// Stable reference (not an inline arrow at the call site) so MetricCell's
// memo actually skips re-rendering the engagementRate cell on unrelated
// parent re-renders — a fresh arrow function every render would otherwise
// look like a changed prop.
const formatPercent = (v) => `${v}%`;

// CPV/CPE are computed and persisted server-side (see computeCpvCpe in
// pluginbackendnode/routes/plans.js) every time either the deliverable costs
// or the average metrics change, so inf.cpv/inf.cpe are always already
// up to date here — no client-side recomputation needed.
const formatCurrency2dp = (value) => `₹${value.toFixed(2)}`;

// Read-only display for every metric that's computed or synced from the
// backend (SocialData profile resolution, the "fetch live details" refresh,
// or the CPV/CPE recompute in computeCpvCpe) — none of these are user-editable
// from this table. See RAPID_INFLUENCER_FIELD_MAP in
// pluginbackendnode/routes/plans.js for exactly which fields this covers and
// why (they'd silently drift out of sync with the real profile otherwise).
const MetricCell = memo(({ value, format = formatCompact }) => (
  <Typography sx={{ fontSize: "inherit" }}>{value != null ? format(value) : "—"}</Typography>
));
MetricCell.displayName = "MetricCell";

// Backs the clickable name/avatar cell below — resolves which brand icon,
// color, and profile noun ("Instagram"/"YouTube"/"LinkedIn") to show for a
// row's platform.
const PLATFORM_META = {
  instagram: { Icon: InstagramIcon, color: "#E1306C", label: "Instagram" },
  youtube: { Icon: YouTubeIcon, color: "#FF0000", label: "YouTube" },
  linkedin: { Icon: LinkedInIcon, color: "#0A66C2", label: "LinkedIn" },
};

// Shared between the enabled and disabled/cooldown icon below so the sync icon
// doesn't visibly jump size when the same button flips between states.
const SYNC_ICON_SX = { fontSize: { xs: 16, sm: 18, md: 20 } };

const RefreshAction = ({ influencer, isRefreshing, onRefresh }) => {
  const canRefresh = canRefreshInfluencer(influencer);
  const cooldown = canRefresh ? getCooldownInfo(influencer) : null;
  const platformLabel = influencer.platform === "youtube" ? "YouTube" : "Instagram";

  if (canRefresh && !cooldown) {
    return (
      <Tooltip title={isRefreshing ? "Fetching live details…" : `Fetch live details from ${platformLabel}`}>
        <span>
          <IconButton
            size="small"
            disabled={isRefreshing}
            onClick={() => onRefresh?.(influencer)}
            sx={{
              color: "#9CA3AF",
              "&:hover": { color: "#160647", bgcolor: "rgba(22,6,71,0.06)" },
              p: { xs: 0.4, sm: 0.6, md: 1 },
            }}
          >
            {isRefreshing ? (
              <CircularProgress size={16} sx={{ color: "#160647" }} />
            ) : (
              <SyncIcon sx={SYNC_ICON_SX} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const disabledReason = !canRefresh
    ? influencer.rapidInfluencerId
      ? "Live refresh is only supported for Instagram and YouTube right now"
      : "Not linked to an influencer record yet"
    : `Recently updated — last fetched ${formatDate(cooldown.lastRefreshedAt)}. Try again after ${formatDate(cooldown.nextEligibleAt)}.`;

  return (
    <Tooltip title={disabledReason}>
      <span>
        <IconButton size="small" disabled sx={{ color: "#E5E7EB", p: { xs: 0.4, sm: 0.6, md: 1 } }}>
          <SyncIcon sx={SYNC_ICON_SX} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

// Sits directly above the per-row RefreshAction column — bulk-refreshes every
// eligible row in this list in one go. Hover previews the breakdown (nothing
// fires until clicked); while a run is in flight, this cell shows live
// progress instead. See PlanSummary.jsx for the breakdown/status computation
// and pluginbackendnode's POST/GET .../refresh-all|refresh-status routes.
const RefreshAllHeaderAction = ({ breakdown, isRefreshing, progress, onTrigger }) => {
  const { total = 0, eligible = 0, cooldown = 0, ineligible = 0 } = breakdown || {};

  if (isRefreshing) {
    const done = progress?.done ?? 0;
    const runTotal = progress?.total ?? 0;
    return (
      <Tooltip title={`Refreshing… ${done} / ${runTotal} done`}>
        <span>
          <IconButton size="small" disabled sx={{ color: "#160647", p: { xs: 0.4, sm: 0.6, md: 1 } }}>
            <CircularProgress size={16} sx={{ color: "#160647" }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const tooltipTitle = (
    <Box sx={{ fontSize: 12, lineHeight: 1.7, py: 0.25 }}>
      <div>Total: {total}</div>
      <div>Recently updated (skipped): {cooldown}</div>
      {ineligible > 0 && <div>Not linked yet (skipped): {ineligible}</div>}
      <div style={{ fontWeight: 700, marginTop: 2 }}>
        {eligible > 0 ? `Click to refresh ${eligible} creator${eligible === 1 ? "" : "s"}` : "Nothing eligible to refresh right now"}
      </div>
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <IconButton
          size="small"
          disabled={eligible === 0}
          onClick={onTrigger}
          sx={{
            color: eligible > 0 ? "#160647" : "#D1D5DB",
            "&:hover": { color: "#FF5200", bgcolor: "rgba(255,82,0,0.08)" },
            p: { xs: 0.4, sm: 0.6, md: 1 },
          }}
        >
          <SyncIcon sx={SYNC_ICON_SX} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

// Small "N/T filled · ₹total" line shown under an active Cost column's own
// name (see summary's shape in PlanSummary.jsx's deliverableCostSummary) —
// identical for both Influencer Cost and Brand Cost, just given a different
// summary map, so the format only has to be gotten right in one place.
// Renders nothing until at least one row has a cost for this deliverable, so
// a freshly-checked column starts blank instead of showing "0/92 · ₹0".
const DeliverableCostSummaryLine = ({ summary, totalCount }) => {
  if (!summary?.count) return null;
  return (
    <Typography sx={{ fontSize: 9, color: "text.secondary", fontWeight: 600, mt: 0.15 }}>
      {summary.count}/{totalCount} · ₹{summary.total.toLocaleString("en-IN")}
    </Typography>
  );
};

// Lets a user check any number of deliverables to show as their own Total
// Cost column for every influencer in the current list, instead of opening
// the edit modal for each row. Checking is additive — unchecking one later
// just hides that column, it never deletes the costs already entered under
// it. Individual rows can still be changed afterwards from their own edit
// modal or by editing a cell directly.
//
// Despite the "deliverable" naming, this component doesn't know or care what
// the options mean — it's reused as-is for all three dynamic column groups
// (Influencer Cost, Brand Cost, and Custom Columns), each just passing its
// own catalog/active-list/create/delete callbacks through these same props.
const DeliverableColumnsMenu = ({ columnLabel, itemLabel = "deliverable", activeDeliverables, onApply, disabled, customDeliverables, onCreateCustomDeliverable, onDeleteCustomDeliverable }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState(activeDeliverables);
  const [newName, setNewName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [deletingName, setDeletingName] = useState(null);

  // Every deliverable comes from the CustomDeliverable collection now — there's no
  // separate hardcoded list merged in on top of it anymore (see
  // pluginbackendnode/scripts/seedDeliverables.js). Checking one just adds its name to
  // activeDeliverables, so every downstream piece (cost cells, CPV/CPE, Last Cost)
  // treats it identically regardless of when/how it was added.
  // Deduped defensively (case-insensitive) — the backend already rejects a name
  // colliding with an existing one, but a stale cache/race shouldn't be able to render
  // two identical MenuItem keys here regardless.
  const options = useMemo(() => {
    const seen = new Set();
    const merged = [];
    for (const option of customDeliverables || []) {
      const key = option.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(option);
    }
    return merged;
  }, [customDeliverables]);

  const handleOpen = (e) => {
    // Reseed from server truth every time the menu opens, so a stale local
    // draft from a previous open (e.g. closed without applying) never lingers.
    setDraft(activeDeliverables);
    setNewName("");
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleToggle = (option) => {
    setDraft((prev) =>
      prev.includes(option) ? prev.filter((d) => d !== option) : [...prev, option]
    );
  };

  const handleCreate = () => {
    const trimmed = newName.trim();
    if (!trimmed || isCreating) return;
    setIsCreating(true);
    Promise.resolve(onCreateCustomDeliverable?.(trimmed))
      .then((result) => {
        const name = result?.name || trimmed;
        setDraft((prev) => (prev.includes(name) ? prev : [...prev, name]));
        setNewName("");
      })
      .catch(() => {})
      .finally(() => setIsCreating(false));
  };

  // The backend rejects this (409) if the label is still referenced by any
  // plan, so failures are expected/normal here, not exceptional — the error
  // message it returns explains why and is surfaced by the caller.
  const handleDelete = (name, e) => {
    e.stopPropagation();
    if (deletingName) return;
    setDeletingName(name);
    Promise.resolve(onDeleteCustomDeliverable?.(name))
      .then(() => {
        setDraft((prev) => prev.filter((d) => d !== name));
      })
      .catch(() => {})
      .finally(() => setDeletingName(null));
  };

  const handleApply = () => {
    handleClose();
    onApply?.(draft);
  };

  return (
    <>
      <Tooltip title={`Choose which ${itemLabel}s to show under ${columnLabel}`}>
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={handleOpen}
            sx={{ color: "#9CA3AF", "&:hover": { color: "#FF5200", bgcolor: "rgba(255,82,0,0.08)" },p:{xs:0.4,sm:0.6,md:1} }}
          >
            <SelectAllIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} autoFocusItem={false}>
        {options.map((option) => (
          <MenuItem key={option} onClick={() => handleToggle(option)} dense sx={{ pr: 0.5 }}>
            <Checkbox checked={draft.includes(option)} size="small" sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={option} primaryTypographyProps={{ fontSize: 13.5 }} />
            {/* Every deliverable is a DB row now, so every one is equally deletable —
                the backend still blocks it (409) if any plan is actually using it. */}
            <Tooltip title={`Delete this ${itemLabel}`}>
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => handleDelete(option, e)}
                  disabled={deletingName === option}
                  sx={{ ml: 1, p: 0.25, color: "#9CA3AF", "&:hover": { color: "error.main" } }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span>
            </Tooltip>
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 1 }}>
          <TextField
            size="small"
            placeholder={`Add a custom ${itemLabel}`}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              // Menu's MenuList owns keydown on its root for roving-focus/
              // type-ahead nav — without stopping propagation here, every
              // keystroke (including Enter) bubbles up to it instead of just
              // reaching this field, so typing here never reliably worked.
              // Escape is the one key deliberately let through, so it still
              // reaches Menu's own close handler instead of getting trapped.
              if (e.key !== "Escape") e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreate();
              }
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{ flex: 1 }}
            inputProps={{ style: { fontSize: 13.5, padding: "6px 8px" } }}
          />
          <Tooltip title={`Add ${itemLabel}`}>
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreate();
                }}
                disabled={!newName.trim() || isCreating}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 1 }}>
          <Button size="small" onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            sx={{ textTransform: "none", boxShadow: "none" }}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </>
  );
};

// Click-to-edit cell used for every editable column — plain text/number at
// rest, a real input only while the one cell being edited is mounted. That
// keeps a large table cheap: with N rows and M editable columns, this avoids
// mounting N*M live inputs at once (which is what an always-editable cell per
// column would cost) — only the single cell someone is actively touching ever
// renders one. Values live in local state until blur/Enter commits them, so
// typing never fires a request per keystroke, and Escape reverts in place
// without committing anything.
// Small dot shown next to a value that carries "who touched this last, and
// when" attribution (Contact Number, Email, Influencer Cost, Brand Cost) —
// one component for all four instead of four ad-hoc tooltips. Renders
// nothing when there's no attribution yet, so every other InlineEditCell
// usage (Reason, POC, Location, Custom Columns, …) is unaffected.
const EditedByIndicator = ({ editedBy, editedAt }) => {
  if (!editedBy) return null;
  return (
    <Tooltip title={`Added by ${editedBy}${editedAt ? ` · ${formatDate(editedAt)}` : ""}`}>
      <Box
        component="span"
        sx={{
          display: "inline-block",
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "#FF5200",
          ml: 0.75,
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
};

const InlineEditCell = memo(({ value, type = "text", required = false, multiline = false, emptyLabel = "—", formatDisplay, inputProps, typographySx, editedBy, editedAt, onCommit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEdit = () => {
    setDraft(value != null ? String(value) : "");
    setEditing(true);
  };

  const commit = (raw) => {
    setEditing(false);
    if (type === "number") {
      const normalized = raw === "" ? null : Number(raw);
      if (normalized !== (value ?? null)) onCommit?.(normalized);
      return;
    }
    const trimmed = raw.trim();
    // A required text field can't be blanked out — silently keep whatever it
    // was rather than sending an edit the backend would 400 on.
    if (required && !trimmed) return;
    if (trimmed !== (value ?? "")) onCommit?.(trimmed);
  };

  if (editing) {
    return (
      <TextField
        autoFocus
        size="small"
        variant="standard"
        type={type}
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        maxRows={multiline ? 6 : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          // Enter commits for single-line fields, but multiline ones need it
          // to insert a newline instead — only Escape-to-cancel applies there.
          if (e.key === "Enter" && !multiline) e.target.blur();
          if (e.key === "Escape") setEditing(false);
        }}
        inputProps={{ style: { fontSize: "inherit" }, ...inputProps }}
        sx={{ width: "100%" }}
      />
    );
  }

  const hasValue = value !== null && value !== undefined && value !== "";
  const display = (
    <Typography
      noWrap={!multiline}
      sx={{
        fontSize: "inherit",
        color: hasValue ? "inherit" : "#9CA3AF",
        ...(multiline && {
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }),
        ...typographySx,
      }}
    >
      {hasValue ? (formatDisplay ? formatDisplay(value) : value) : emptyLabel}
    </Typography>
  );

  const content = multiline && hasValue ? <Tooltip title={value}>{display}</Tooltip> : display;

  return (
    <Box
      onClick={startEdit}
      sx={{
        cursor: "text",
        borderRadius: 1,
        px: 0.5,
        mx: -0.5,
        "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
      }}
    >
      {editedBy ? (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>{content}</Box>
          <EditedByIndicator editedBy={editedBy} editedAt={editedAt} />
        </Box>
      ) : (
        content
      )}
    </Box>
  );
});
InlineEditCell.displayName = "InlineEditCell";

// Language is a multi-select from a fixed, searchable list (not free text —
// see LANGUAGE_OPTIONS) — a different enough shape from InlineEditCell's plain
// scalar text/number that it gets its own small click-to-edit component rather
// than a third branch bolted onto that one. Selections are staged locally while
// the dropdown is open (so picking several languages doesn't fire a request per
// click) and committed once, on close — same "one write per real change" spirit
// as InlineEditCell, just batched over multiple picks instead of one.
const LanguageCell = memo(({ value, editedBy, editedAt, onCommit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const languages = value || [];

  const startEdit = () => {
    setDraft(languages);
    setEditing(true);
  };

  const handleClose = (_, reason) => {
    setEditing(false);
    // Escape cancels the draft entirely, same as InlineEditCell's Escape-to-revert.
    if (reason === "escape") return;
    const changed =
      draft.length !== languages.length || draft.some((l, i) => l !== languages[i]);
    if (changed) onCommit?.(draft);
  };

  if (editing) {
    return (
      <Autocomplete
        multiple
        autoFocus
        openOnFocus
        disableCloseOnSelect
        size="small"
        options={LANGUAGE_OPTIONS}
        value={draft}
        onChange={(_, newValue) => setDraft(newValue)}
        onClose={handleClose}
        renderTags={(tagValues, getTagProps) =>
          tagValues.map((option, index) => (
            <Chip
              label={option}
              size="small"
              {...getTagProps({ index })}
              key={option}
              sx={{ height: 20, fontSize: 11 }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            variant="standard"
            placeholder={languages.length ? "" : "Search languages"}
            sx={{ "& .MuiInputBase-root": { fontSize: "inherit" } }}
          />
        )}
        sx={{ minWidth: { xs: 150, sm: 190, md: 220 } }}
      />
    );
  }

  return (
    <Box
      onClick={startEdit}
      sx={{
        cursor: "text",
        borderRadius: 1,
        px: 0.5,
        mx: -0.5,
        "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: "inherit", color: languages.length ? "inherit" : "#9CA3AF" }}
        >
          {languages.length ? languages.join(", ") : "Add language"}
        </Typography>
        <EditedByIndicator editedBy={editedBy} editedAt={editedAt} />
      </Box>
    </Box>
  );
});
LanguageCell.displayName = "LanguageCell";

// Kept in sync with CONTACT_TYPE_OPTIONS in pluginbackendnode/models/plan.js —
// no shared config layer between the two repos.
const CONTACT_TYPE_OPTIONS = ["creator", "manager", "agency"];
// Matches ContactSchema's `number` validator in pluginbackendnode/models/rapidInfluencer.js —
// checked client-side too so an obviously-bad number never round-trips to the
// server just to bounce back as a 400.
const CONTACT_NUMBER_REGEX = /^[0-9]{6,15}$/;
const blankContact = (isPrimary) => ({ type: "creator", name: "", countryCode: "+91", number: "", isPrimary });

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const blankEmail = (isPrimary) => ({ email: "", isPrimary });

// An influencer can have any number of contacts (creator/manager/agency), so
// this isn't a scalar-value cell like InlineEditCell — it opens a small popover
// listing every number, with add/remove and a "primary" toggle. Unlike every
// other cell in this table, nothing here auto-commits on blur/close — this is
// destructive to a record shared across every plan the influencer is in, so it
// follows the explicit Save/Cancel shape of DeliverableColumnsMenu (above)
// instead: closing the popover any other way (backdrop click, Escape) just
// discards the draft, same as that menu's onClose.
const ContactNumbersCell = memo(({ value, linked, editedBy, editedAt, onCommit }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState([]);
  const contacts = value || [];

  // Numbers already saved on the master record are locked (see the disabled
  // fields below) — this is sensitive identity data, and a fat-fingered edit
  // in place would silently overwrite a correct, possibly verified number.
  // A wrong saved number has to be explicitly removed and re-added instead.
  const startEdit = (e) => {
    setDraft(contacts.length ? contacts.map((c) => ({ ...c, _existing: true, _removed: false })) : [blankContact(true)]);
    setAnchorEl(e.currentTarget);
  };

  // Backdrop click / Escape — discard the draft entirely, same as
  // DeliverableColumnsMenu's onClose. Only the Save button below persists.
  const handleCancel = () => setAnchorEl(null);

  const handleSave = () => {
    setAnchorEl(null);
    const cleaned = draft
      .filter((c) => !c._removed)
      .map((c) => ({ type: c.type, name: c.name, countryCode: c.countryCode, number: c.number.trim(), isPrimary: c.isPrimary }))
      .filter((c) => CONTACT_NUMBER_REGEX.test(c.number));
    if (!cleaned.some((c) => c.isPrimary) && cleaned.length) cleaned[0].isPrimary = true;

    const changed = JSON.stringify(cleaned) !== JSON.stringify(contacts);
    if (changed) onCommit?.(cleaned);
  };

  const updateEntry = (i, patch) => {
    setDraft((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  };

  // A saved number is never spliced out immediately — a stray click on X would
  // otherwise be one click away from deleting real data with no way back. It's
  // instead flagged _removed (rendered below as a greyed "Undo" row) and only
  // actually dropped from the list when Save is clicked. A not-yet-saved row
  // has nothing to lose, so X there just deletes it from the draft outright.
  const removeEntry = (i) => {
    setDraft((prev) => prev.map((c, idx) => {
      if (idx !== i) return c;
      return c._existing ? { ...c, _removed: true } : null;
    }).filter(Boolean));
  };

  const undoRemove = (i) => updateEntry(i, { _removed: false });

  // Exclusive — only one number is ever primary, so picking a new one must
  // clear the flag everywhere else in the same update (a plain updateEntry(i,
  // { isPrimary: true }) would leave the previous primary's flag set too).
  const setPrimary = (i) => {
    setDraft((prev) => prev.map((c, idx) => ({ ...c, isPrimary: idx === i })));
  };

  const addEntry = () => setDraft((prev) => [...prev, { ...blankContact(prev.length === 0), _existing: false }]);

  if (!linked) {
    return (
      <Tooltip title="Not linked to an influencer record yet">
        <Typography noWrap sx={{ fontSize: "inherit", color: "#D1D5DB" }}>—</Typography>
      </Tooltip>
    );
  }

  const primary = contacts.find((c) => c.isPrimary) || contacts[0];

  return (
    <>
      <Box
        onClick={startEdit}
        sx={{
          cursor: "pointer",
          borderRadius: 1,
          px: 0.5,
          mx: -0.5,
          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: "inherit", color: primary ? "inherit" : "#9CA3AF" }}>
            {primary ? `${primary.countryCode}${primary.number}` : "Add number"}
          </Typography>
          <EditedByIndicator editedBy={editedBy} editedAt={editedAt} />
        </Box>
        {contacts.length > 1 && (
          <Typography sx={{ color: "#9CA3AF", fontSize: 11 }}>+{contacts.length - 1} more</Typography>
        )}
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, width: 340, display: "flex", flexDirection: "column", gap: 1 }}>
          {draft.map((c, i) => {
            if (c._removed) {
              return (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                  <Typography sx={{ flex: 1, fontSize: 13, color: "#9CA3AF", textDecoration: "line-through" }} noWrap>
                    {c.countryCode}{c.number}
                  </Typography>
                  <Typography sx={{ color: "#9CA3AF", fontSize: 12 }}>Removed</Typography>
                  <Button size="small" onClick={() => undoRemove(i)} sx={{ textTransform: "none", minWidth: 0 }}>
                    Undo
                  </Button>
                </Box>
              );
            }

            const invalid = c.number.length > 0 && !CONTACT_NUMBER_REGEX.test(c.number);
            const locked = c._existing;
            const fieldSx = locked ? { "& .MuiInputBase-input": { color: "#9CA3AF" } } : undefined;
            return (
              <Box key={i} sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}>
                <Tooltip title={locked ? "Saved numbers can't be edited — remove and add it again to change it" : ""}>
                  <TextField
                    select
                    size="small"
                    variant="standard"
                    value={c.type}
                    disabled={locked}
                    onChange={(e) => updateEntry(i, { type: e.target.value })}
                    sx={{ width: 84, ...fieldSx }}
                  >
                    {CONTACT_TYPE_OPTIONS.map((t) => (
                      <MenuItem key={t} value={t} sx={{ fontSize: 13, textTransform: "capitalize" }}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                </Tooltip>
                <Tooltip title={locked ? "Saved numbers can't be edited — remove and add it again to change it" : ""}>
                  <TextField
                    size="small"
                    variant="standard"
                    value={c.countryCode}
                    disabled={locked}
                    onChange={(e) => updateEntry(i, { countryCode: e.target.value })}
                    sx={{ width: 44, ...fieldSx }}
                  />
                </Tooltip>
                <Tooltip title={locked ? "Saved numbers can't be edited — remove and add it again to change it" : ""}>
                  <TextField
                    autoFocus={!locked && i === draft.length - 1}
                    size="small"
                    variant="standard"
                    placeholder="Phone number"
                    value={c.number}
                    disabled={locked}
                    error={invalid}
                    helperText={invalid ? "6-15 digits" : ""}
                    onChange={(e) => updateEntry(i, { number: e.target.value.replace(/[^0-9]/g, "") })}
                    sx={{ flex: 1, ...fieldSx }}
                  />
                </Tooltip>
                <Tooltip title={c.isPrimary ? "Primary number" : "Set as primary"}>
                  <IconButton size="small" onClick={() => setPrimary(i)} sx={{ mt: 0.5 }}>
                    {c.isPrimary ? (
                      <StarIcon fontSize="small" sx={{ color: "#FF5200" }} />
                    ) : (
                      <StarBorderIcon fontSize="small" sx={{ color: "#D1D5DB" }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove this number">
                  <IconButton size="small" onClick={() => removeEntry(i)} sx={{ mt: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
          <Button
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={addEntry}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Add another number
          </Button>
          <Divider sx={{ mt: 0.5 }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button size="small" onClick={handleCancel} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              sx={{ textTransform: "none", boxShadow: "none" }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
});
ContactNumbersCell.displayName = "ContactNumbersCell";

// Same shape/behavior as ContactNumbersCell above (synced to the master
// RapidInfluencer record, saved entries locked — remove and re-add instead of
// editing in place), just one field simpler: no "type" categorization, emails
// don't have a creator/manager/agency axis the way phone numbers do.
const EmailsCell = memo(({ value, linked, editedBy, editedAt, onCommit }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState([]);
  const emails = value || [];

  const startEdit = (e) => {
    setDraft(emails.length ? emails.map((em) => ({ ...em, _existing: true, _removed: false })) : [blankEmail(true)]);
    setAnchorEl(e.currentTarget);
  };

  const handleCancel = () => setAnchorEl(null);

  const handleSave = () => {
    setAnchorEl(null);
    const cleaned = draft
      .filter((em) => !em._removed)
      .map((em) => ({ email: em.email.trim().toLowerCase(), isPrimary: em.isPrimary }))
      .filter((em) => EMAIL_REGEX.test(em.email));
    if (!cleaned.some((em) => em.isPrimary) && cleaned.length) cleaned[0].isPrimary = true;

    const changed = JSON.stringify(cleaned) !== JSON.stringify(emails);
    if (changed) onCommit?.(cleaned);
  };

  const updateEntry = (i, patch) => {
    setDraft((prev) => prev.map((em, idx) => (idx === i ? { ...em, ...patch } : em)));
  };

  const removeEntry = (i) => {
    setDraft((prev) => prev.map((em, idx) => {
      if (idx !== i) return em;
      return em._existing ? { ...em, _removed: true } : null;
    }).filter(Boolean));
  };

  const undoRemove = (i) => updateEntry(i, { _removed: false });

  const setPrimary = (i) => {
    setDraft((prev) => prev.map((em, idx) => ({ ...em, isPrimary: idx === i })));
  };

  const addEntry = () => setDraft((prev) => [...prev, { ...blankEmail(prev.length === 0), _existing: false }]);

  if (!linked) {
    return (
      <Tooltip title="Not linked to an influencer record yet">
        <Typography noWrap sx={{ fontSize: "inherit", color: "#D1D5DB" }}>—</Typography>
      </Tooltip>
    );
  }

  const primary = emails.find((em) => em.isPrimary) || emails[0];

  return (
    <>
      <Box
        onClick={startEdit}
        sx={{
          cursor: "pointer",
          borderRadius: 1,
          px: 0.5,
          mx: -0.5,
          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: "inherit", color: primary ? "inherit" : "#9CA3AF" }}>
            {primary ? primary.email : "Add email"}
          </Typography>
          <EditedByIndicator editedBy={editedBy} editedAt={editedAt} />
        </Box>
        {emails.length > 1 && (
          <Typography sx={{ color: "#9CA3AF", fontSize: 11 }}>+{emails.length - 1} more</Typography>
        )}
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, width: 320, display: "flex", flexDirection: "column", gap: 1 }}>
          {draft.map((em, i) => {
            if (em._removed) {
              return (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                  <Typography sx={{ flex: 1, fontSize: 13, color: "#9CA3AF", textDecoration: "line-through" }} noWrap>
                    {em.email}
                  </Typography>
                  <Typography sx={{ color: "#9CA3AF", fontSize: 12 }}>Removed</Typography>
                  <Button size="small" onClick={() => undoRemove(i)} sx={{ textTransform: "none", minWidth: 0 }}>
                    Undo
                  </Button>
                </Box>
              );
            }

            const invalid = em.email.length > 0 && !EMAIL_REGEX.test(em.email);
            const locked = em._existing;
            const fieldSx = locked ? { "& .MuiInputBase-input": { color: "#9CA3AF" } } : undefined;
            return (
              <Box key={i} sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}>
                <Tooltip title={locked ? "Saved emails can't be edited — remove and add it again to change it" : ""}>
                  <TextField
                    autoFocus={!locked && i === draft.length - 1}
                    size="small"
                    variant="standard"
                    placeholder="Email address"
                    value={em.email}
                    disabled={locked}
                    error={invalid}
                    helperText={invalid ? "Enter a valid email" : ""}
                    onChange={(e) => updateEntry(i, { email: e.target.value })}
                    sx={{ flex: 1, ...fieldSx }}
                  />
                </Tooltip>
                <Tooltip title={em.isPrimary ? "Primary email" : "Set as primary"}>
                  <IconButton size="small" onClick={() => setPrimary(i)} sx={{ mt: 0.5 }}>
                    {em.isPrimary ? (
                      <StarIcon fontSize="small" sx={{ color: "#FF5200" }} />
                    ) : (
                      <StarBorderIcon fontSize="small" sx={{ color: "#D1D5DB" }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove this email">
                  <IconButton size="small" onClick={() => removeEntry(i)} sx={{ mt: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
          <Button
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={addEntry}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Add another email
          </Button>
          <Divider sx={{ mt: 0.5 }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button size="small" onClick={handleCancel} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              sx={{ textTransform: "none", boxShadow: "none" }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
});
EmailsCell.displayName = "EmailsCell";

const InfluencerTable = ({
  influencers,
  visibleColumns,
  platform,
  onRemove,
  onRefresh,
  selectedIds,
  onToggleSelectOne,
  onToggleSelectAll,
  allSelected,
  someSelected,
  activeDeliverables,
  onApplyDeliverables,
  isApplyingDeliverables,
  activeBrandDeliverables,
  onApplyBrandDeliverables,
  isApplyingBrandDeliverables,
  onSetBrandDeliverableCost,
  customDeliverables,
  onCreateCustomDeliverable,
  onDeleteCustomDeliverable,
  onSetDeliverableCost,
  deliverableCostTotals,
  brandDeliverableCostTotals,
  lastPlanCostSummary,
  totalInfluencerCount,
  activeCustomFields,
  onApplyCustomFields,
  isApplyingCustomFields,
  customFieldOptions,
  onCreateCustomField,
  onDeleteCustomField,
  onSetCustomFieldValue,
  onUpdateField,
  onSetContactNumbers,
  onSetEmails,
  refreshingIds,
  sortConfig,
  onSortChange,
  refreshAllBreakdown,
  isRefreshingAll,
  refreshAllProgress,
  onTriggerRefreshAll,
  lastViewedId,
  onProfileLinkClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const columns = ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key));
  // The one review modal instance for whichever row's Reviews cell was
  // clicked — a single shared instance, not one per row, since only one can
  // ever be open at a time.
  const [reviewTarget, setReviewTarget] = useState(null);
  // Zero active deliverables → a single prompt column pointing at the menu.
  // One or more → one editable cost column per checked deliverable.
  const deliverableColumns = useMemo(() => {
    if (!activeDeliverables?.length) {
      return [{ key: "totalCost", label: "Influencer Cost", empty: true }];
    }
    return activeDeliverables.map((d) => ({ key: `deliverableCost:${d}`, label: d, deliverable: d }));
  }, [activeDeliverables]);
  // Same mechanism as deliverableColumns above, for the independent Brand
  // Cost column group.
  const brandDeliverableColumns = useMemo(() => {
    if (!activeBrandDeliverables?.length) {
      return [{ key: "brandCost", label: "Brand Cost", empty: true }];
    }
    return activeBrandDeliverables.map((d) => ({ key: `brandDeliverableCost:${d}`, label: d, deliverable: d }));
  }, [activeBrandDeliverables]);
  // Same mechanism as deliverableColumns/brandDeliverableColumns above, for
  // the independent, freeform Custom Columns group.
  const customFieldColumns = useMemo(() => {
    if (!activeCustomFields?.length) {
      return [{ key: "customFields", label: "Custom Columns", empty: true }];
    }
    return activeCustomFields.map((f) => ({ key: `customField:${f}`, label: f, field: f }));
  }, [activeCustomFields]);
  // A list is single-platform (enforced when profiles are added), so the whole
  // column can safely relabel — YouTube has no "followers" concept, just subscribers.
  const followersLabel = platform === "youtube" ? "Subscribers" : "Followers";
  const HEADER_CELL_STYLES = {
    color: "text.secondary",
    fontWeight: 500,
    borderBottom: `2px solid ${GRID_LINE_COLOR}`,
    borderRight: `1px solid ${GRID_LINE_COLOR}`,
    whiteSpace: "nowrap",
    fontSize: {
      xs: 11,
      sm: 12,
      md: 13,
    },
    px: {
      xs: 1,
      sm: 1.5,
      md: 2,
    },
    // TableSortLabel only shows its arrow on hover/active by default (opacity
    // 0 otherwise) — every sortable column gets one always visible instead,
    // so the sort affordance doesn't have to be discovered by hovering each
    // header. The active column's own arrow still wins out at opacity 1 (its
    // own MUI rule outranks this one), so "currently sorted by" still stands
    // out from the rest.
    "& .MuiTableSortLabel-icon": {
      opacity: 0.5,
    },
  };

  const BODY_CELL_STYLES = {
    borderBottom: `1px solid ${GRID_LINE_COLOR}`,
    borderRight: `1px solid ${GRID_LINE_COLOR}`,
    whiteSpace: "nowrap",
    fontSize: {
      xs: 11,
      sm: 12,
      md: 13,
    },
    px: {
      xs: 1,
      sm: 1.5,
      md: 2,
    },
  };
  return (
    <>
    <TableContainer sx={{ overflowX: "auto", width: "100%", WebkitOverflowScrolling: "touch", "&::-webkit-scrollbar": { height: 6 } }}>
    <Table
      sx={{
        minWidth: { xs: 900, sm: 1100, md: 1300 },
        // MUI's default `border-collapse: collapse` shares each border between
        // two cells rather than letting either paint it alone — combined with
        // the sticky Name/checkbox columns, that starves the browser of a
        // clean per-cell paint boundary and lets scrolled-past column content
        // bleed through them mid-scroll (worst on mobile WebKit). `separate`
        // fixes that; visually identical here since only borderBottom/-Right
        // are ever set, so nothing doubles up at shared edges.
        borderCollapse: "separate",
        borderSpacing: 0,
      }}
      size={isMobile ? 'small' : 'medium'}
    >
      <TableHead>
        <TableRow>
          <TableCell
            padding="checkbox"
            sx={{
              ...HEADER_CELL_STYLES,
              position: "sticky",
              left: 0,
              zIndex: 2,
              width: STICKY_CHECKBOX_WIDTH,
              minWidth: STICKY_CHECKBOX_WIDTH,
              px: 1,
              bgcolor: "#fff",
            }}
          >
            <Checkbox
              size="small"
              checked={Boolean(allSelected)}
              indeterminate={Boolean(someSelected)}
              onChange={() => onToggleSelectAll?.()}
            />
          </TableCell>
          <TableCell
            sx={{
              ...HEADER_CELL_STYLES,
              position: "sticky",
              left: STICKY_CHECKBOX_WIDTH,
              zIndex: 2,
              bgcolor: "#fff",
              boxShadow: "2px 0 4px -2px rgba(0,0,0,0.12)",
            }}
          >
            <TableSortLabel
              active={sortConfig?.key === "name"}
              direction={sortConfig?.key === "name" ? sortConfig.direction : "asc"}
              onClick={() => onSortChange?.("name")}
            >
              Influencer Name
            </TableSortLabel>
          </TableCell>
          {columns.map((col) => {
            if (col.key !== "totalCost" && col.key !== "brandCost" && col.key !== "customFields") {
              const label = col.key === "followers" ? followersLabel : col.label;
              return (
                <TableCell key={col.key} sx={HEADER_CELL_STYLES}>
                  {col.sortable === false ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>{label}</Box>
                  ) : (
                    <TableSortLabel
                      active={sortConfig?.key === col.key}
                      direction={sortConfig?.key === col.key ? sortConfig.direction : "asc"}
                      onClick={() => onSortChange?.(col.key)}
                    >
                      {label}
                    </TableSortLabel>
                  )}
                  {col.key === "lastPlanCost" && (
                    <DeliverableCostSummaryLine summary={lastPlanCostSummary} totalCount={totalInfluencerCount} />
                  )}
                </TableCell>
              );
            }
            if (col.key === "totalCost") {
              return (
                <Fragment key="totalCost-group">
                  {deliverableColumns.map((dc, i) => (
                    <TableCell key={dc.key} sx={{ ...HEADER_CELL_STYLES, borderLeft: `2px solid ${INFLUENCER_COST_ACCENT}` }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        {dc.empty ? (
                          dc.label
                        ) : (
                          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25, py: 0.25 }}>
                            <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", color: INFLUENCER_COST_ACCENT }}>
                              Influencer
                            </Typography>
                            <Tooltip title={dc.label}>
                              <Typography noWrap sx={{ fontSize: "inherit", maxWidth: { xs: 90, sm: 110, md: 130 } }}>
                                {dc.label}
                              </Typography>
                            </Tooltip>
                            <DeliverableCostSummaryLine summary={deliverableCostTotals?.[dc.deliverable]} totalCount={totalInfluencerCount} />
                          </Box>
                        )}
                        {i === 0 && (
                          <DeliverableColumnsMenu
                            columnLabel="Influencer Cost"
                            activeDeliverables={activeDeliverables}
                            onApply={onApplyDeliverables}
                            disabled={isApplyingDeliverables}
                            customDeliverables={customDeliverables}
                            onCreateCustomDeliverable={onCreateCustomDeliverable}
                            onDeleteCustomDeliverable={onDeleteCustomDeliverable}
                          />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </Fragment>
              );
            }
            if (col.key === "brandCost") {
              return (
                <Fragment key="brandCost-group">
                  {brandDeliverableColumns.map((dc, i) => (
                    <TableCell key={dc.key} sx={{ ...HEADER_CELL_STYLES, borderLeft: `2px solid ${BRAND_COST_ACCENT}` }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        {dc.empty ? (
                          dc.label
                        ) : (
                          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25, py: 0.25 }}>
                            <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", color: BRAND_COST_ACCENT }}>
                              Brand
                            </Typography>
                            <Tooltip title={dc.label}>
                              <Typography noWrap sx={{ fontSize: "inherit", maxWidth: { xs: 90, sm: 110, md: 130 } }}>
                                {dc.label}
                              </Typography>
                            </Tooltip>
                            <DeliverableCostSummaryLine summary={brandDeliverableCostTotals?.[dc.deliverable]} totalCount={totalInfluencerCount} />
                          </Box>
                        )}
                        {i === 0 && (
                          <DeliverableColumnsMenu
                            columnLabel="Brand Cost"
                            activeDeliverables={activeBrandDeliverables}
                            onApply={onApplyBrandDeliverables}
                            disabled={isApplyingBrandDeliverables}
                            customDeliverables={customDeliverables}
                            onCreateCustomDeliverable={onCreateCustomDeliverable}
                            onDeleteCustomDeliverable={onDeleteCustomDeliverable}
                          />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </Fragment>
              );
            }
            // The only key left once the three branches above are excluded.
            return (
              <Fragment key="customFields-group">
                {customFieldColumns.map((dc, i) => (
                  <TableCell key={dc.key} sx={{ ...HEADER_CELL_STYLES, borderLeft: `2px solid ${CUSTOM_FIELD_ACCENT}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                      {dc.empty ? (
                        dc.label
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25, py: 0.25 }}>
                          <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", color: CUSTOM_FIELD_ACCENT }}>
                            Custom
                          </Typography>
                          <Tooltip title={dc.label}>
                            <Typography noWrap sx={{ fontSize: "inherit", maxWidth: { xs: 90, sm: 110, md: 130 } }}>
                              {dc.label}
                            </Typography>
                          </Tooltip>
                        </Box>
                      )}
                      {i === 0 && (
                        <DeliverableColumnsMenu
                          columnLabel="Custom Columns"
                          itemLabel="column"
                          activeDeliverables={activeCustomFields}
                          onApply={onApplyCustomFields}
                          disabled={isApplyingCustomFields}
                          customDeliverables={customFieldOptions}
                          onCreateCustomDeliverable={onCreateCustomField}
                          onDeleteCustomDeliverable={onDeleteCustomField}
                        />
                      )}
                    </Box>
                  </TableCell>
                ))}
              </Fragment>
            );
          })}
          <TableCell sx={{ border: 0, minWidth: { xs: 120, sm: 150, md: 172 }, px: { xs: 0.5, sm: 1, md: 2 } }} align="right">
            <RefreshAllHeaderAction
              breakdown={refreshAllBreakdown}
              isRefreshing={isRefreshingAll}
              progress={refreshAllProgress}
              onTrigger={onTriggerRefreshAll}
            />
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {influencers.map((inf) => {
          const platformMeta = PLATFORM_META[inf.platform];
          const linkable = Boolean(inf.profileUrl);
          const isLastViewed = Boolean(lastViewedId) && inf.id === lastViewedId;
          return (
            <TableRow
              key={inf.id}
              hover
              sx={{
                ...(isLastViewed && { bgcolor: "#FFF5F0", boxShadow: "inset 3px 0 0 #FF5200" }),
                // TableRow's own `hover` prop only recolors the <tr> — invisible
                // behind the sticky cells' opaque background below, so their
                // hover shade is reapplied explicitly here via the same class.
                // Uses an opaque grey rather than theme.palette.action.hover's
                // translucent rgba — a see-through hover tint on the sticky
                // cells would let horizontally-scrolled content bleed through
                // them again, the exact bug the opaque base background (and
                // the border-collapse fix alongside it) exists to prevent.
                "&:hover .sticky-col": { bgcolor: isLastViewed ? "#FFF5F0" : "#F5F5F5" },
              }}
            >
              <TableCell
                padding="checkbox"
                className="sticky-col"
                sx={{
                  ...BODY_CELL_STYLES,
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  width: STICKY_CHECKBOX_WIDTH,
                  minWidth: STICKY_CHECKBOX_WIDTH,
                  px: 1,
                  bgcolor: isLastViewed ? "#FFF5F0" : "#fff",
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedIds?.has(inf.id) ?? false}
                  onChange={() => onToggleSelectOne?.(inf.id)}
                />
              </TableCell>
              <TableCell
                className="sticky-col"
                sx={{
                  ...BODY_CELL_STYLES,
                  position: "sticky",
                  left: STICKY_CHECKBOX_WIDTH,
                  zIndex: 1,
                  bgcolor: isLastViewed ? "#FFF5F0" : "#fff",
                  boxShadow: "2px 0 4px -2px rgba(0,0,0,0.12)",
                }}
              >
                <Tooltip title={linkable ? `Open ${platformMeta?.label || "profile"} in a new tab` : ""}>
                  <Box
                    component={linkable ? "a" : "div"}
                    href={linkable ? inf.profileUrl : undefined}
                    target={linkable ? "_blank" : undefined}
                    rel={linkable ? "noopener noreferrer" : undefined}
                    onClick={linkable ? () => onProfileLinkClick?.(inf.id) : undefined}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1, sm: 1.25, md: 1.5 },
                      textDecoration: "none",
                      color: "inherit",
                      cursor: linkable ? "pointer" : "default",
                      borderRadius: 1,
                      px: 0.5,
                      mx: -0.5,
                      ...(linkable && {
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.04)",
                          "& .influencer-name": { textDecoration: "underline" },
                        },
                      }),
                    }}
                  >
                    <Avatar src={inf.avatar} sx={{ width: { xs: 28, sm: 32, md: 36 }, height: { xs: 28, sm: 32, md: 36 } }}>
                      {inf.name?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ minWidth: 0, maxWidth: { xs: 100, sm: 140, md: 180 } }}>
                          <Typography
                            noWrap
                            className="influencer-name"
                            sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: { xs: 12, sm: 13, md: 14 } }}
                          >
                            {inf.name}
                          </Typography>
                        </Box>
                        {inf.verified && (
                          <VerifiedIcon sx={{ fontSize: { xs: 12, sm: 14, md: 15 }, color: "#e0a100", flexShrink: 0 }} />
                        )}
                        {platformMeta && (
                          <platformMeta.Icon sx={{ fontSize: { xs: 11, sm: 12, md: 13 }, color: platformMeta.color, flexShrink: 0 }} />
                        )}
                      </Box>
                      {formatUsername(inf.username) && (
                        <Typography sx={{ color: "#9CA3AF", lineHeight: 1.3, fontSize: { xs: 10, sm: 11, md: 12.5 }, maxWidth: { xs: 100, sm: 140, md: 180 } }} noWrap>
                          @{formatUsername(inf.username)}
                        </Typography>
                      )}
                      {isLastViewed && (
                        <Chip
                          icon={<VisibilityOutlinedIcon sx={{ fontSize: "12px !important" }} />}
                          label="Last viewed"
                          size="small"
                          sx={{
                            height: 18,
                            mt: 0.25,
                            fontSize: 10,
                            fontWeight: 700,
                            bgcolor: "#FFE4D6",
                            color: "#CC4200",
                            "& .MuiChip-icon": { color: "#FF5200", ml: "5px" },
                            "& .MuiChip-label": { px: "5px" },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Tooltip>
              </TableCell>

              {visibleColumns.includes("followers") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <MetricCell value={inf.followers} />
                </TableCell>
              )}
              {visibleColumns.includes("likesAvg") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <MetricCell value={inf.likesAvg} />
                </TableCell>
              )}
              {visibleColumns.includes("viewsAvg") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <MetricCell value={inf.viewsAvg} />
                </TableCell>
              )}
              {visibleColumns.includes("commentsAvg") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <MetricCell value={inf.commentsAvg} />
                </TableCell>
              )}
              {visibleColumns.includes("sharesAvg") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <MetricCell value={inf.sharesAvg} />
                </TableCell>
              )}
              {visibleColumns.includes("repostsAvg") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <MetricCell value={inf.repostsAvg} />
                </TableCell>
              )}
              {visibleColumns.includes("engagementRate") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <MetricCell value={inf.engagementRate} format={formatPercent} />
                </TableCell>
              )}
              {visibleColumns.includes("totalCost") && (
                activeDeliverables?.length ? (
                  activeDeliverables.map((d) => {
                    const entry = inf.deliverableCosts?.find((dc) => dc.deliverable === d);
                    return (
                      <TableCell key={d} sx={{ ...BODY_CELL_STYLES, borderLeft: `2px solid ${INFLUENCER_COST_ACCENT}4D` }}>
                        <InlineEditCell
                          value={entry?.cost ?? null}
                          type="number"
                          emptyLabel="Add cost"
                          inputProps={{ min: 0 }}
                          formatDisplay={(v) => `₹${v}`}
                          editedBy={entry?.editedBy}
                          editedAt={entry?.editedAt}
                          onCommit={(cost) => onSetDeliverableCost?.(inf.id, d, cost)}
                        />
                      </TableCell>
                    );
                  })
                ) : (
                  <TableCell sx={{ ...BODY_CELL_STYLES, borderLeft: `2px solid ${INFLUENCER_COST_ACCENT}4D` }}>
                    <Typography sx={{ color: "#9CA3AF", fontStyle: "italic", fontSize: "inherit" }}>
                      Select deliverables above
                    </Typography>
                  </TableCell>
                )
              )}
              {visibleColumns.includes("brandCost") && (
                activeBrandDeliverables?.length ? (
                  activeBrandDeliverables.map((d) => {
                    const entry = inf.brandDeliverableCosts?.find((dc) => dc.deliverable === d);
                    return (
                      <TableCell key={d} sx={{ ...BODY_CELL_STYLES, borderLeft: `2px solid ${BRAND_COST_ACCENT}4D` }}>
                        <InlineEditCell
                          value={entry?.cost ?? null}
                          type="number"
                          emptyLabel="Add cost"
                          inputProps={{ min: 0 }}
                          formatDisplay={(v) => `₹${v}`}
                          editedBy={entry?.editedBy}
                          editedAt={entry?.editedAt}
                          onCommit={(cost) => onSetBrandDeliverableCost?.(inf.id, d, cost)}
                        />
                      </TableCell>
                    );
                  })
                ) : (
                  <TableCell sx={{ ...BODY_CELL_STYLES, borderLeft: `2px solid ${BRAND_COST_ACCENT}4D` }}>
                    <Typography sx={{ color: "#9CA3AF", fontStyle: "italic", fontSize: "inherit" }}>
                      Select deliverables above
                    </Typography>
                  </TableCell>
                )
              )}
              {visibleColumns.includes("cpv") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <MetricCell value={inf.cpv} format={formatCurrency2dp} />
                </TableCell>
              )}
              {visibleColumns.includes("cpe") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <MetricCell value={inf.cpe} format={formatCurrency2dp} />
                </TableCell>
              )}
              {visibleColumns.includes("lastPaidCost") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <Typography sx={{ fontSize: "inherit" }}>
                    {inf.lastPaidCost != null ? `₹${inf.lastPaidCost}` : "—"}
                  </Typography>
                  {inf.lastPaidDeliverable && (
                    <Typography sx={{ color: "#9CA3AF", fontSize: 11.5, mt: 0.25 }} noWrap>
                      {inf.lastPaidDeliverable}
                    </Typography>
                  )}
                </TableCell>
              )}
              {visibleColumns.includes("lastPlanCost") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <Tooltip
                    title={
                      inf.lastPlanCost != null
                        ? `${inf.lastPlanCostPlanName || "Another plan"} · ${formatDate(inf.lastPlanCostPlanCreatedOn)}`
                        : ""
                    }
                  >
                    <span>
                      <Typography sx={{ fontSize: "inherit" }}>
                        {inf.lastPlanCost != null ? `₹${inf.lastPlanCost}` : "—"}
                      </Typography>
                      {inf.lastPlanCostDeliverables?.length > 0 && (
                        <Typography sx={{ color: "#9CA3AF", fontSize: 11.5, mt: 0.25 }} noWrap>
                          {inf.lastPlanCostDeliverables.join(", ")}
                        </Typography>
                      )}
                    </span>
                  </Tooltip>
                </TableCell>
              )}
              {visibleColumns.includes("reviews") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  {inf.rapidInfluencerId ? (
                    <Box
                      onClick={() => setReviewTarget(inf)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                        borderRadius: 1,
                        px: 0.5,
                        mx: -0.5,
                        "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                      }}
                    >
                      {inf.totalRatings > 0 ? (
                        <>
                          <Typography sx={{ fontSize: 13, color: "#D97706", lineHeight: 1 }}>★</Typography>
                          <Typography sx={{ fontSize: "inherit", fontWeight: 600 }}>
                            {Number(inf.avgRating || 0).toFixed(1)}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "#9CA3AF" }}>
                            ({inf.totalRatings})
                          </Typography>
                        </>
                      ) : (
                        <Typography sx={{ fontSize: "inherit", color: "#9CA3AF", fontStyle: "italic" }}>
                          Rate
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Tooltip title="Not linked to an influencer record yet">
                      <Typography sx={{ fontSize: "inherit", color: "#E5E7EB" }}>—</Typography>
                    </Tooltip>
                  )}
                </TableCell>
              )}
              {visibleColumns.includes("contactNumber") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <ContactNumbersCell
                    value={inf.contactNumbers}
                    linked={Boolean(inf.rapidInfluencerId)}
                    editedBy={inf.contactNumbersUpdatedBy}
                    editedAt={inf.contactNumbersUpdatedAt}
                    onCommit={(contacts) => onSetContactNumbers?.(inf.id, contacts)}
                  />
                </TableCell>
              )}
              {visibleColumns.includes("email") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <EmailsCell
                    value={inf.emails}
                    linked={Boolean(inf.rapidInfluencerId)}
                    editedBy={inf.emailsUpdatedBy}
                    editedAt={inf.emailsUpdatedAt}
                    onCommit={(emails) => onSetEmails?.(inf.id, emails)}
                  />
                </TableCell>
              )}
              {visibleColumns.includes("category") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <InlineEditCell
                    value={inf.category}
                    editedBy={inf.categoryUpdatedBy}
                    editedAt={inf.categoryUpdatedAt}
                    onCommit={(value) => onUpdateField?.(inf.id, "category", value)}
                  />
                </TableCell>
              )}
              {visibleColumns.includes("language") && (
                <TableCell sx={{ ...BODY_CELL_STYLES, maxWidth: { xs: 120, sm: 150, md: 190 } }}>
                  <LanguageCell
                    value={inf.language}
                    editedBy={inf.languageUpdatedBy}
                    editedAt={inf.languageUpdatedAt}
                    onCommit={(value) => onUpdateField?.(inf.id, "language", value)}
                  />
                </TableCell>
              )}
              {visibleColumns.includes("location") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <InlineEditCell
                    value={inf.location}
                    editedBy={inf.locationUpdatedBy}
                    editedAt={inf.locationUpdatedAt}
                    onCommit={(value) => onUpdateField?.(inf.id, "location", value)}
                  />
                </TableCell>
              )}
              {visibleColumns.includes("poc") && (
                <TableCell sx={BODY_CELL_STYLES}>
                  <InlineEditCell
                    value={inf.poc}
                    onCommit={(value) => onUpdateField?.(inf.id, "poc", value)}
                  />
                </TableCell>
              )}
              {visibleColumns.includes("reason") && (
                <TableCell sx={{ ...BODY_CELL_STYLES, whiteSpace: "normal", maxWidth: { xs: 140, sm: 180, md: 240 } }}>
                  <InlineEditCell
                    value={inf.reason}
                    multiline
                    emptyLabel="Add reason"
                    editedBy={inf.reasonUpdatedBy}
                    editedAt={inf.reasonUpdatedAt}
                    onCommit={(value) => onUpdateField?.(inf.id, "reason", value)}
                  />
                </TableCell>
              )}
              {visibleColumns.includes("customFields") && (
                activeCustomFields?.length ? (
                  activeCustomFields.map((f) => {
                    const entry = inf.customFields?.find((cf) => cf.field === f);
                    return (
                      <TableCell key={f} sx={{ ...BODY_CELL_STYLES, borderLeft: `2px solid ${CUSTOM_FIELD_ACCENT}4D` }}>
                        <InlineEditCell
                          value={entry?.value ?? null}
                          emptyLabel="Add note"
                          multiline
                          onCommit={(value) => onSetCustomFieldValue?.(inf.id, f, value)}
                        />
                      </TableCell>
                    );
                  })
                ) : (
                  <TableCell sx={{ ...BODY_CELL_STYLES, borderLeft: `2px solid ${CUSTOM_FIELD_ACCENT}4D` }}>
                    <Typography sx={{ color: "#9CA3AF", fontStyle: "italic", fontSize: "inherit" }}>
                      Select custom columns above
                    </Typography>
                  </TableCell>
                )
              )}
              <TableCell sx={{ ...BODY_CELL_STYLES, minWidth: { xs: 120, sm: 140, md: 172 }, px: { xs: 0.5, sm: 1, md: 2 } }} align="right">
                <RefreshAction
                  influencer={inf}
                  isRefreshing={refreshingIds?.has(inf.id) ?? false}
                  onRefresh={onRefresh}
                />
                {inf.rapidInfluencerId ? (
                  <Tooltip
                    title={
                      inf.hasHistory
                        ? "Past engagement found — view history"
                        : "No previous engagement yet — view profile"
                    }
                  >
                    <IconButton
                      size={isMobile ? "small" : "medium"}
                      component="a"
                      href={`/payment-history/${inf.rapidInfluencerId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: inf.hasHistory ? "#FF5200" : "#D1D5DB",
                        "&:hover": {
                          color: inf.hasHistory ? "primary.dark" : "#9CA3AF",
                          bgcolor: inf.hasHistory ? "rgba(255,82,0,0.08)" : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      {inf.hasHistory ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Not linked to an influencer record yet">
                    <span>
                      <IconButton size={isMobile ? "small" : "medium"} disabled sx={{ color: "#E5E7EB" }}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Remove from list">
                  <IconButton
                    size="small"
                    onClick={() => onRemove?.(inf)}
                    sx={{
                      color: "#9CA3AF",
                      "&:hover": { color: "error.main", bgcolor: "rgba(239,68,68,0.08)" },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    </TableContainer>
    <InfluencerReviewModal
      open={Boolean(reviewTarget)}
      onClose={() => setReviewTarget(null)}
      creatorId={reviewTarget?.rapidInfluencerId}
      name={reviewTarget?.name}
      avatar={reviewTarget?.avatar}
    />
    </>
  );
};

export default InfluencerTable;
export { ALL_COLUMNS, InlineEditCell };
